import { getJwtExpiryMs, getJwtSubject } from "utils/jwt";

/**
 * Renew the access token this long BEFORE its `exp`. The legacy API refuses an expired token on
 * `POST /users/refresh_token` (grace 0, BE `REFRESH_TOKEN_GRACE_SECONDS`), so a refresh has to happen
 * while the token is still live — a 401 is too late, there is nothing left to refresh it with.
 * Thirty minutes leaves room for a throttled background tab (Chrome fires hidden-tab timers up to a
 * minute late) and a few retries.
 */
export const SESSION_REFRESH_LEAD_MS = 30 * 60_000;

/** Spread the renewal over this window so several open tabs don't all refresh in the same second. */
export const SESSION_REFRESH_JITTER_MS = 2 * 60_000;

/**
 * Never renew more often than this. A token whose lifetime is shorter than the lead (an admin
 * impersonation token is capped at 60 minutes and stays capped across refreshes) would otherwise be
 * due again the moment it arrives, and the module would refresh in a tight loop.
 */
export const SESSION_REFRESH_MIN_INTERVAL_MS = 5 * 60_000;

/** After a refresh that got no usable token (network error, 429, 5xx), try again this much later. */
export const SESSION_REFRESH_RETRY_MS = 60_000;

/** What one refresh attempt produced: a fresh token, or nothing (the attempt failed). */
export type SessionRefreshResult = string | null;

export interface SessionRefresherDeps {
  /** Current token from storage; null when logged out. Re-read on every decision. */
  getToken: () => string | null;
  /** Persist the fresh token wherever requests read it from (storage + the HTTP client). */
  applyToken: (token: string) => void;
  /**
   * One call to the refresh endpoint, sent with the CURRENT token. Resolves the new token, or null when
   * there is none to use. An invalid session is not this function's business: the shared HTTP
   * interceptor answers a 401 with the app's logout, which clears the token this module re-reads.
   */
  refresh: (email: string) => Promise<SessionRefreshResult>;
  now?: () => number;
  random?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export interface SessionRefresher {
  /** Re-read the token and (re)schedule. Safe to call any time — on start, tab focus, `storage` events. */
  check: () => void;
  stop: () => void;
}

/**
 * Keeps a signed-in session alive by renewing its token shortly before it expires.
 *
 * The schedule is derived from the token itself (its `exp`), never from when the page loaded, so a tab
 * that slept through the planned time renews on the next `check()`, and a token another tab already
 * renewed is simply rescheduled. A token whose `exp` has already passed is left alone: the server
 * would refuse it, and the next request's 401 ends the session through the normal logout path.
 */
export const createSessionRefresher = ({
  getToken,
  applyToken,
  refresh,
  now = Date.now,
  random = Math.random,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
}: SessionRefresherDeps): SessionRefresher => {
  let timer: unknown = null;
  let inFlightFor: string | null = null;
  let stopped = false;
  // The token the current timer was planned for, and when it fires. Lets check() keep an existing
  // plan (and its jitter) instead of re-rolling it on every focus event.
  let plannedFor: string | null = null;
  let retryAt: number | null = null;
  let lastSuccessAt: number | null = null;

  const cancelTimer = () => {
    if (timer !== null) clearTimer(timer);
    timer = null;
  };

  const schedule = (token: string, delayMs: number) => {
    cancelTimer();
    plannedFor = token;
    timer = setTimer(() => {
      timer = null;
      void run();
    }, Math.max(0, delayMs));
  };

  const run = async () => {
    if (stopped) return;
    const token = getToken();
    if (!token || inFlightFor === token) return;
    const expMs = getJwtExpiryMs(token);
    if (expMs === null || expMs <= now()) return;

    inFlightFor = token;
    let fresh: SessionRefreshResult = null;
    try {
      fresh = await refresh(getJwtSubject(token) ?? "");
    } catch {
      fresh = null;
    } finally {
      inFlightFor = null;
    }
    if (stopped) return;

    // Only write a result for the session it was requested for. During the await the user may have
    // logged out, logged in as someone else, or another tab may have renewed — none of those may be
    // overwritten with this response.
    if (getToken() !== token) {
      check();

      return;
    }
    if (fresh && getJwtExpiryMs(fresh) !== null) {
      retryAt = null;
      lastSuccessAt = now();
      applyToken(fresh);
      check();

      return;
    }
    retryAt = now() + SESSION_REFRESH_RETRY_MS;
    schedule(token, SESSION_REFRESH_RETRY_MS);
  };

  const check = () => {
    if (stopped) return;
    const token = getToken();
    const expMs = getJwtExpiryMs(token);
    if (!token || expMs === null || expMs <= now()) {
      cancelTimer();
      plannedFor = null;
      retryAt = null;

      return;
    }
    if (inFlightFor === token) return;
    // Same token, plan still pending → keep it. A pending retry keeps its own (later) time.
    if (plannedFor === token && timer !== null) return;
    if (plannedFor !== token) retryAt = null;

    let dueAt = retryAt ?? expMs - SESSION_REFRESH_LEAD_MS - random() * SESSION_REFRESH_JITTER_MS;
    if (lastSuccessAt !== null) dueAt = Math.max(dueAt, lastSuccessAt + SESSION_REFRESH_MIN_INTERVAL_MS);
    // Past the token's own expiry the server refuses it — don't plan a call that can only fail.
    if (dueAt >= expMs) {
      cancelTimer();
      plannedFor = token;

      return;
    }
    schedule(token, dueAt - now());
  };

  const stop = () => {
    stopped = true;
    cancelTimer();
    plannedFor = null;
    retryAt = null;
  };

  return { check, stop };
};
